import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { 
  useUserProfile, 
  useUserPosts, 
  useFollowStats, 
  useFollowStatus, 
  useFollowUser,
  useLikePost,
  useCommentOnPost,
  useDeletePost
} from "../hooks/useFeed";
import { useCurrentUser } from "../hooks/useAuth";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import FeedPostsList from "../components/feed/FeedPostsList";
import ProfileImage from "../components/profileImage";
import { useMainPageTitle } from "../contexts/PageContext";
import { useTranslation } from "react-i18next";

const FeedProfile = () => {
  const { updatePageTitle } = useMainPageTitle();
  const {t} = useTranslation();
  const { id } = useParams();
  const [comment, setComment] = useState("");
  const [activeCommentPost, setActiveCommentPost] = useState(null);

  // Get current user
  const { data: currentUser } = useCurrentUser();

  // Get profile data
  const { 
    data: userProfile, 
    isLoading: profileLoading, 
    error: profileError 
  } = useUserProfile(id);

  // Get posts data
  const { 
    data: postsData, 
    isLoading: postsLoading, 
    error: postsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useUserPosts(id);

  // Get follow data
  const { data: followStats = { followersCount: 0, followingCount: 0 } } = useFollowStats(id);
  const { data: isFollowing = false } = useFollowStatus(id);

  // Mutations
  const followUserMutation = useFollowUser();
  const likePostMutation = useLikePost();
  const commentMutation = useCommentOnPost();
  const deletePostMutation = useDeletePost();

  // Update page title when userProfile is available
  useEffect(() => {
      if (userProfile?.userName) {
        updatePageTitle(userProfile.userName, userProfile.userName + " " + t("feed.profile"));
      }
  }, [updatePageTitle, t, userProfile?.userName]);

  // Get posts from pages
  const posts = postsData?.pages?.flatMap(page => page.data) || [];

  // Loading state - only show full loading if profile is loading
  if (profileLoading) {
    return <LoadingSpinner message="Loading profile..." />;
  }

  // Error state
  if (profileError || postsError) {
    return <ErrorMessage message="Failed to load profile" />;
  }

  // User not found
  if (!userProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">User not found</h2>
          <p className="text-gray-600">The user you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  // Event handlers
  const handleFollowToggle = async () => {
    try {
      await followUserMutation.mutateAsync({ followingId: id });
    } catch (error) {
      console.error("Error toggling follow:", error);
    }
  };

  const handleLike = async (postId) => {
    try {
      await likePostMutation.mutateAsync(postId);
    } catch (error) {
      console.error("❌ Error liking post:", error);
    }
  };

  const handleCommentSubmit = async (postId) => {
    if (!comment.trim()) return;
    try {
      await commentMutation.mutateAsync({ 
        postId, 
        content: comment, 
        currentUser 
      });
      setComment("");
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await deletePostMutation.mutateAsync(postId);
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Profile Header */}
      <div className="relative">
        {/* Cover Background */}
        <div className="h-48 bg-gradient-to-r from-[#E05720] via-[#E05720]/80 to-[#E05720]/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-30"></div>
        </div>
        
        {/* Profile Content */}
        <div className="relative -mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                  {/* Enhanced Profile Picture */}
                  <div className="relative group">
                    <div className="w-32 h-32 rounded-full bg-gradient-to-r from-[#E05720] to-[#E05720]/80 p-1 shadow-2xl">
                      <ProfileImage
                        image={userProfile.profilePicture}
                        alt={userProfile.userName}
                        className="w-full h-full rounded-full object-cover bg-white transition-transform duration-300 group-hover:scale-105"
                        user={userProfile}
                      />
                    </div>
                  </div>

                  {/* Enhanced Profile Info */}
                  <div className="flex-1 text-center sm:text-left">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                      <div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                          {userProfile.userName}
                        </h1>
                        <p className="text-gray-600 text-lg">
                          {userProfile.accountType && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#E05720]/10 text-[#E05720] capitalize">
                              {userProfile.accountType}
                            </span>
                          )}
                        </p>
                      </div>
                      
                      {/* Enhanced Follow Button - Hide if viewing own profile */}
                      {(() => {
                        const isOwnProfile = (
                          (currentUser?.id && currentUser.id === userProfile?._id) || 
                          (currentUser?._id && currentUser._id === userProfile?._id) ||
                          (currentUser?.id && currentUser.id === userProfile?.id) || 
                          (currentUser?._id && currentUser._id === userProfile?.id)
                        );
                        return !isOwnProfile && currentUser && userProfile;
                      })() && (
                        <div className="mt-4 sm:mt-0">
                          <button
                            onClick={handleFollowToggle}
                            disabled={followUserMutation.isPending}
                            className={`relative overflow-hidden px-8 py-3 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105 shadow-lg ${
                              isFollowing
                                ? "bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300"
                                : "bg-gradient-to-r from-[#E05720] to-[#E05720]/80 text-white hover:from-[#E05720]/90 hover:to-[#E05720]/70 shadow-[#E05720]/25"
                            } ${followUserMutation.isPending ? 'opacity-75 cursor-not-allowed' : ''}`}
                          >
                            {followUserMutation.isPending ? (
                              <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                <span>Loading...</span>
                              </div>
                            ) : (
                              <>
                                {isFollowing ? (
                                  <div className="flex items-center space-x-2">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span>Following</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    <span>Follow</span>
                                  </div>
                                )}
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Enhanced Stats */}
                    <div className="grid grid-cols-3 gap-6 max-w-md mx-auto sm:mx-0">
                      <div className="text-center p-4 bg-gradient-to-br from-[#E05720]/5 to-[#E05720]/10 rounded-xl transition-transform duration-200 hover:scale-105">
                        <div className="text-2xl font-bold text-[#E05720]">{posts.length}</div>
                        <div className="text-sm text-[#E05720]/80 font-medium">Posts</div>
                      </div>
                      <div className="text-center p-4 bg-gradient-to-br from-[#E05720]/5 to-[#E05720]/10 rounded-xl transition-transform duration-200 hover:scale-105">
                        <div className="text-2xl font-bold text-[#E05720]">{followStats.followersCount}</div>
                        <div className="text-sm text-[#E05720]/80 font-medium">Followers</div>
                      </div>
                      <div className="text-center p-4 bg-gradient-to-br from-[#E05720]/5 to-[#E05720]/10 rounded-xl transition-transform duration-200 hover:scale-105">
                        <div className="text-2xl font-bold text-[#E05720]">{followStats.followingCount}</div>
                        <div className="text-sm text-[#E05720]/80 font-medium">Following</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Posts Section */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Posts</h2>
        
        {postsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#E05720] border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600">Loading posts...</p>
            </div>
          </div>
        ) : postsError ? (
          <div className="text-center py-12">
            <p className="text-red-600">Failed to load posts</p>
          </div>
        ) : (
          <FeedPostsList
            posts={posts}
            currentUser={currentUser}
            onLike={handleLike}
            onComment={handleCommentSubmit}
            onDelete={handleDeletePost}
            comment={comment}
            setComment={setComment}
            activeCommentPost={activeCommentPost}
            setActiveCommentPost={setActiveCommentPost}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            loadMore={loadMore}
            commentMutation={commentMutation}
            likePostMutation={likePostMutation}
            deletePostMutation={deletePostMutation}
            isLiking={likePostMutation.isPending}
            isCommenting={commentMutation.isPending}
          />
        )}
      </div>
    </div>
  );
};

export default FeedProfile;
